import { Job } from '@models/Job'
import { jobSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createJob as createJobAPI,
  deleteJob as deleteJobAPI,
  fetchJobById,
  fetchJobs,
  updateJob as updateJobAPI,
} from '@services/JobService'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Job> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'job',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getJobsSuccess,
  setQuery: setJobQuery,
  reset: resetJobState,
  resetQueryAndIds: resetJobQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: string]: Job }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectJobs = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedJob = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectJobById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectJobsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedJob =
  (job: Job | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Job>(job)))

export const getJobs = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(fetchJobs(params), [jobSchema], slice.actions)

export const createJob = (job: Job) =>
  SBAPI.CreateDispatched(createJobAPI(job), jobSchema, slice.actions)

export const getJobWithId = (id: number) =>
  SBAPI.FetchDispatched<Job>(fetchJobById(id), jobSchema, slice.actions)

export const updateJob = (job: Job) =>
  SBAPI.UpdateDispatched<Job>(updateJobAPI(job), job, slice.actions)

export const deleteJob = (job: Job) =>
  SBAPI.DestroyDispatched<Job>(deleteJobAPI(job), job, slice.actions)
